import { isAffordableApplicationFlow } from "src/pages/Account/Register/unitHelpers";
import { getCurrentLease, isActiveLeaseStatus } from "./helperLease";

/**
 * Shared application status → action logic.
 *
 * This is the single source of truth for "what can a resident do with this
 * application right now", used by both the Settings › Applications page
 * (`ApplicationList.tsx`) and the homepage Pending Applications card so the
 * two can't drift. Ported verbatim from the original inline branching in
 * `ApplicationList.tsx`.
 */

export type ApplicationActionKind = "continue" | "pay" | "verify-income";

export interface ApplicationAction {
  kind: ApplicationActionKind;
  /** i18n key for the CTA label. */
  labelKey: string;
  /** English fallback for the CTA label. */
  labelFallback: string;
  /** Builds the navigation target for the CTA. */
  buildPath: () => string;
}

const getPropertyId = (app: any): string =>
  app?.property?.id || app?.propertyId || "";

/**
 * True when the application fee is already settled (or the application is
 * otherwise closed). The payment page must not stay open in these states —
 * reloading `/application/payment` after a successful pay-first charge would
 * otherwise re-hit the vacancy check and falsely show "unit no longer available".
 */
export function isApplicationFeeSettled(app: any): boolean {
  if (!app) return false;
  const paymentStatus = String(app.paymentStatus ?? "").toLowerCase();
  if (paymentStatus === "paid" || paymentStatus === "refunded") return true;
  const status = String(app.status ?? "").toLowerCase();
  return (
    status === "completed" ||
    status === "denied" ||
    status === "cancelled" ||
    status === "canceled"
  );
}

/**
 * Resolve the actionable next step for an application, or `null` when there is
 * nothing for the resident to do (e.g. `denied`, `cancelled`, or a `completed`
 * application whose income verification is already satisfied).
 */
export function getApplicationAction(app: any): ApplicationAction | null {
  if (!app) return null;
  const propertyId = getPropertyId(app);

  if (app.status === "pending") {
    const formRoute = isAffordableApplicationFlow(
      { number: app.unit },
      app.property
    )
      ? "/application/affordable/form"
      : "/application/form";
    return {
      kind: "continue",
      labelKey: "account.registerProspect.viewApplicationButton",
      labelFallback: "Continue Application",
      buildPath: () => {
        const search =
          typeof window !== "undefined" ? window.location.search : "";
        return `${formRoute}${search ? search : "?"}&applicationId=${
          app.id
        }&propertyId=${propertyId}&leaseId=${app.lease?.id || ""}`;
      },
    };
  }

  if (app.status === "submitted") {
    // Fee already captured (or refunded) — do not send them back to payment.
    // Proofup / home routing is handled by the completed branch below once
    // finalize flips status; until then there is no resident CTA here.
    if (isApplicationFeeSettled(app)) {
      return null;
    }
    return {
      kind: "pay",
      labelKey: "account.registerProspect.viewPaymentButton",
      labelFallback: "Pay Application Fee",
      buildPath: () =>
        `/application/payment?applicationId=${app.id}${
          app.lease?.id ? `&leaseid=${app.lease.id}` : ""
        }`,
    };
  }

  // Income verification (ProofUp) happens after payment and is skippable, so a
  // paid ("completed") application may still owe verification. Keep it resumable
  // — but never nag an applicant who already verified via the old in-wizard step
  // (legacy `incomeVerification` marker, for mid-transition apps).
  if (
    app.status === "completed" &&
    app.property?.affordableHousing !== true &&
    app.applicationData?.proofupStatus === "needed" &&
    app.applicationData?.incomeVerification !== "completed"
  ) {
    return {
      kind: "verify-income",
      labelKey: "account.incomeVerification.verifyIncomeCta",
      labelFallback: "Verify Income",
      buildPath: () =>
        `/application/income-verification?leaseid=${
          app.lease?.id || ""
        }&personid=${
          app.person?.id || ""
        }&propertyid=${propertyId}&applicationId=${app.id}`,
    };
  }

  return null;
}

/** True when the application has an actionable next step. */
export function isActionableApplication(app: any): boolean {
  return getApplicationAction(app) !== null;
}

/**
 * Property ids where the resident already holds an ACTIVE (moved-in) lease.
 * Used to suppress stale pending applications for a unit the resident already
 * lives in. Reads the eager `person.leaseLinks[].lease` on the user payload,
 * plus `getCurrentLease` as a fallback source.
 */
export function getActivePropertyIds(userData: any): Set<string> {
  const ids = new Set<string>();

  const leaseLinks = userData?.user?.person?.leaseLinks;
  if (Array.isArray(leaseLinks)) {
    for (const link of leaseLinks) {
      const lease = link?.lease;
      const propId = lease?.property?.id;
      if (lease && isActiveLeaseStatus(lease.Status) && propId) {
        ids.add(propId);
      }
    }
  }

  const currentLease = getCurrentLease(userData);
  if (
    currentLease &&
    isActiveLeaseStatus(currentLease.Status) &&
    currentLease.property?.id
  ) {
    ids.add(currentLease.property.id);
  }

  return ids;
}

/**
 * Select the applications to surface on the homepage: only those with an
 * actionable next step, excluding any whose property matches a lease the
 * resident is already active on. Sorted newest-first.
 */
export function selectPendingApplications(apps: any[], userData: any): any[] {
  if (!Array.isArray(apps)) return [];
  const activePropertyIds = getActivePropertyIds(userData);

  return apps
    .filter((app) => isActionableApplication(app))
    .filter((app) => {
      const propId = getPropertyId(app);
      return !(propId && activePropertyIds.has(propId));
    })
    .sort((a, b) => {
      const at = new Date(a?.updatedDate || a?.createdDate || 0).getTime();
      const bt = new Date(b?.updatedDate || b?.createdDate || 0).getTime();
      return bt - at;
    });
}
